import { useEffect, useMemo, useRef, useState } from "react";
import type { PlayerRef } from "@remotion/player";
import { useFullState } from "../../vendor/src/features/remotion-editor/editor/utils/use-context";
import { sceneNarration } from "./scene-narration";

// Cache decoded originals, never edited media. One active audition across scene cards.
const buffers = new Map<string, Promise<AudioBuffer>>();
let stopActive: (() => void) | null = null;
async function originalAudio(
  url: string,
  context: AudioContext,
): Promise<AudioBuffer> {
  const source = new URL(url);
  if (
    (!["https:", "blob:"].includes(source.protocol) &&
      !/^data:audio\//.test(url)) ||
    source.username ||
    source.password
  )
    throw new Error("음성 원본 주소를 확인해 주세요.");
  let pending = buffers.get(url);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(url, { credentials: "omit", cache: "no-cache" });
      if (!response.ok) throw new Error("음성 원본을 불러오지 못했습니다.");
      if (Number(response.headers.get("content-length")) > 64 * 1024 * 1024)
        throw new Error("이 큰 음성 파일은 정밀 편집 재생으로 확인해 주세요.");
      const data = await response.arrayBuffer();
      if (data.byteLength > 64 * 1024 * 1024)
        throw new Error("음성 파일이 미리 듣기 범위를 넘었습니다.");
      return context.decodeAudioData(data);
    })();
    buffers.set(url, pending);
    pending.catch(() => {
      if (buffers.get(url) === pending) buffers.delete(url);
    });
    if (buffers.size > 8) buffers.delete(buffers.keys().next().value!);
  }
  return pending;
}

export function SceneNarrationPreview({
  start,
  end,
  playerRef,
}: {
  start: number;
  end: number;
  playerRef: React.RefObject<PlayerRef | null>;
}) {
  const { undoableState: document } = useFullState();
  const plan = useMemo(
    () => sceneNarration(document, start, end),
    [document, start, end],
  );
  const stopRef = useRef<(() => void) | null>(null);
  const mounted = useRef(true);
  const [status, setStatus] = useState<"idle" | "loading" | "playing">("idle");
  const [error, setError] = useState("");
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false; stopRef.current?.();};
  }, []);
  useEffect(() => () => stopRef.current?.(), [plan]);
  const available = plan.ok && plan.windows.length > 0;
  async function play() {
    if (stopRef.current) {
      stopRef.current();
      return;
    }
    if (!plan.ok || !plan.windows.length) return;
    stopActive?.();
    const player = playerRef.current;
    player?.pause();
    if (typeof AudioContext === "undefined") {
      setError("이 브라우저는 음성 미리 듣기를 지원하지 않습니다.");
      return;
    }
    let context: AudioContext;
    try {
      context = new AudioContext();
    } catch {
      setError("오디오 장치를 열지 못했습니다. 다시 시도해 주세요.");
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const nodes: AudioBufferSourceNode[] = [];
    const stop = () => {
      if (cancelled) return;
      cancelled = true;
      player?.removeEventListener('play', stop);
      clearTimeout(timer);
      nodes.forEach((node) => {
        try {
          node.stop();
        } catch {
          /* already stopped */
        }
        node.disconnect();
      });
      void context.close().catch(() => {});
      if (stopActive === stop) stopActive = null;
      if (stopRef.current === stop) stopRef.current = null;
      if (mounted.current) setStatus("idle");
    };
    stopRef.current = stop;
    stopActive = stop;
    player?.addEventListener('play', stop);
    setStatus("loading");
    setError("");
    try {
      await context.resume();
      const decoded = await Promise.all(
        plan.windows.map((window) => originalAudio(window.url, context)),
      );
      if (cancelled) return;
      for (let i = 0; i < plan.windows.length; i++) {
        const window = plan.windows[i]!,
          buffer = decoded[i]!;
        if (
          window.sourceStart + window.duration * window.rate >
          buffer.duration + 1 / document.fps
        )
          throw new Error(
            "씬 구간이 음성 원본 길이를 넘습니다. 정밀 편집에서 구간을 확인해 주세요.",
          );
      }
      const at = context.currentTime + 0.03;
      plan.windows.forEach((window, i) => {
        const node = context.createBufferSource(),
          gain = context.createGain();
        node.buffer = decoded[i]!;
        node.playbackRate.value = window.rate;
        gain.gain.setValueCurveAtTime(
          new Float32Array(window.gain),
          at + window.delay,
          window.duration,
        );
        node.connect(gain);
        gain.connect(context.destination);
        nodes.push(node);
        node.start(at + window.delay, window.sourceStart);
        node.stop(at + window.delay + window.duration);
      });
      setStatus("playing");
      timer = setTimeout(
        stop,
        (0.03 + (end - start) / document.fps) * 1000 + 20,
      );
    } catch (reason) {
      if (!cancelled)
        setError(
          reason instanceof Error
            ? reason.message
            : "음성을 재생하지 못했습니다.",
        );
      stop();
    }
  }
  return (
    <div className="text-xs">
      <button
        type="button"
        disabled={!available}
        onClick={() => void play()}
        title={
          !plan.ok
            ? plan.reason
            : "나레이션으로 지정된 트랙의 현재 씬 구간만 듣습니다."
        }
      >
        {status === "loading"
          ? "불러오기 취소"
          : status === "playing"
            ? "음성 멈추기"
            : "씬 음성 미리 듣기"}
      </button>
      {!available && (
        <span>{plan.ok ? " 이 구간의 나레이션 없음" : plan.reason}</span>
      )}
      {error && <p role="status">{error}</p>}
    </div>
  );
}
